"use client";

import { useQueryClient } from "@tanstack/react-query";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useRef } from "react";

import { useAuthStore } from "@/auth/authStore";
import {
  activateTrader,
  checkTraderActivation,
  type TraderActivation,
} from "@/auth/traderActivation";
import {
  assertCanFundTraderRegistration,
  InsufficientTraderRentError,
} from "@/auth/traderRegistrationFunding";
import { useSolanaWallet } from "@/lib/tx/useSolanaWallet";

import { useOnboardingStore } from "./onboardingStore";

const normalizeErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return fallback;
};

const pendingActivations = new Map<string, Promise<TraderActivation>>();

const runOnce = <T,>(
  cache: Map<string, Promise<T>>,
  key: string,
  request: () => Promise<T>
): Promise<T> => {
  const pending = cache.get(key);
  if (pending !== undefined) return pending;

  const run = request().finally(() => {
    if (cache.get(key) === run) {
      cache.delete(key);
    }
  });
  cache.set(key, run);
  return run;
};

const assertCanFundTraderRegistrationIfReadable = async (
  authority: string
): Promise<void> => {
  try {
    await assertCanFundTraderRegistration(authority);
  } catch (error) {
    if (error instanceof InsufficientTraderRentError) {
      throw error;
    }
    console.warn("Unable to verify trader registration rent", error);
  }
};

export const OnboardingManager = () => {
  const accessCode = useOnboardingStore((state) => state.accessCode);
  const pubkey = useOnboardingStore((state) => state.pubkey);
  const setError = useOnboardingStore((state) => state.setError);
  const setStep = useOnboardingStore((state) => state.setStep);
  const step = useOnboardingStore((state) => state.step);
  const { signer } = useSolanaWallet();
  const queryClient = useQueryClient();
  const router = useRouter();
  const pathname = usePathname();
  const finishingRef = useRef(false);

  useEffect(() => {
    if (step !== "activating" || pubkey === null) return;

    if (signer === null) {
      setError({
        title: "Activation failed",
        description: "This wallet does not support transaction signing.",
        recoverTo: "accessCodeEntry",
      });
      return;
    }

    let cancelled = false;
    const trimmedAccessCode = accessCode.trim();

    void (async () => {
      try {
        const activation = await runOnce(
          pendingActivations,
          `activate:${pubkey}:${trimmedAccessCode}`,
          async () => {
            await assertCanFundTraderRegistrationIfReadable(pubkey);
            await activateTrader({
              authority: pubkey,
              referralCode:
                trimmedAccessCode.length > 0 ? trimmedAccessCode : undefined,
              signer,
            });
            return checkTraderActivation(pubkey);
          }
        );
        if (cancelled) return;

        if (!activation.isPhoenixTrader) {
          setError({
            title: "Activation failed",
            description:
              "We couldn't confirm that this wallet is activated. Please try again.",
            recoverTo: "accessCodeEntry",
          });
          return;
        }

        useAuthStore.getState().setActivationStatus("activated");
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ["builderState", pubkey],
          }),
          queryClient.invalidateQueries({
            queryKey: ["traderCollateral", pubkey],
          }),
        ]);
        if (cancelled) return;
        setStep("done");
      } catch (error) {
        if (cancelled) return;
        const insufficientRent = error instanceof InsufficientTraderRentError;
        setError({
          title: insufficientRent ? "Insufficient SOL" : "Activation failed",
          description: normalizeErrorMessage(
            error,
            "We couldn't set up this account. Please try again."
          ),
          recoverTo: "accessCodeEntry",
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accessCode, pubkey, queryClient, setError, setStep, signer, step]);

  useEffect(() => {
    if (step !== "done" || finishingRef.current) return;
    finishingRef.current = true;

    const timeoutId = window.setTimeout(() => {
      useOnboardingStore.getState().dismissModal();
      finishingRef.current = false;
      if (pathname === "/") {
        router.replace("/dashboard");
      }
    }, 650);

    return () => {
      window.clearTimeout(timeoutId);
      finishingRef.current = false;
    };
  }, [pathname, router, step]);

  return null;
};
